import React, { FunctionComponent } from 'react';
import {
  ChapterNavigationItem,
  ChapterNavigationItemDisabled,
  ChapterNavigationItemMobile,
  LecturerMaterialBrake,
  MaterialLabel,
  MaterialLabelMobile,
  MaterialNavigationSidebar,
  MaterialNavigationSidebarMobile,
  PillNameMobile,
  SubjectLabel,
} from '../Styles/MaterialNavigation';
import { ISideMenu } from '../Interfaces/ISideMenu';
import { lockedIcon } from '../../../Assets/images/locked';
import ChapterCollapse from './ChapterCollapse';
import { chevronIcon } from '../../../Assets/images/Icons';
import MaterialIconProvider from './MaterialIconProvider';
import PASS_STATUS from '../../../__core/Enums/PassStatus';
import MATERIAL_ACCESS from '../../../__core/Enums/MaterialAccess';
import { havePermissions } from '../../../__core/Providers/Permissions/Permissions';
import { IMaterialForMenu } from '../Interfaces/IMaterialForMenu';
import COURSE_ROLES from '../../../__core/Enums/CourseRoles';
import MenuMaterialStatus from './MenuMaterialStatus';
import ProgressIcon from '../../../__core/Components/ProgressStatuses/ProgressIcon';
import ReactTooltip from 'react-tooltip';

type MaterialNavigationProps = {
  toggleSidebar: () => void;
  sidebarCollapse: boolean;
  menuItems: ISideMenu;
  slug: string;
  instanceId: string;
  userRoles: Array<string>;
};

const MaterialNavigation: FunctionComponent<MaterialNavigationProps> = ({
  sidebarCollapse,
  toggleSidebar,
  menuItems,
  slug,
  instanceId,
  userRoles,
}) => {
  const iconTransform = sidebarCollapse ? 'rotate(270deg)' : 'rotate(90deg)';

  const getMaterialUrl = (materialId: string, slug: string): string =>
    `/material/${instanceId}/${slug}/${materialId}`;

  const shouldMaterialRender = (material: IMaterialForMenu): boolean =>
    havePermissions(
      [
        COURSE_ROLES.ROLE_LECTURER,
        COURSE_ROLES.ROLE_MENTOR,
        COURSE_ROLES.ROLE_CS,
      ],
      userRoles
    ) ||
    (havePermissions([COURSE_ROLES.ROLE_USER], userRoles) &&
      (material.access === MATERIAL_ACCESS.ALL ||
        material.access === MATERIAL_ACCESS.STUDENT));

  return (
    <>
      <MaterialNavigationSidebar collapse={sidebarCollapse}>
        <button className="collapseButton" onClick={toggleSidebar}>
          <div style={{ transform: iconTransform }}>{chevronIcon}</div>
        </button>
        <div className="menu-items-container">
          {menuItems.chapters.map((chapter) => {
            return chapter.access ? (
              <ChapterCollapse
                key={chapter.id}
                defaultOpen={`${menuItems.number}-${chapter.number}` === slug}
                showArrow={!sidebarCollapse}
                titleContent={
                  <ChapterNavigationItem>
                    <div className="label">{chapter.name}</div>
                    {chapter.progress && (
                      <>
                        <span
                          data-tip
                          data-for={chapter.id}
                          className="progress-icon"
                        >
                          <ProgressIcon
                            points={chapter.progress.done}
                            maxPoints={chapter.progress.all}
                          />
                        </span>
                        <ReactTooltip
                          id={chapter.id}
                          arrowColor="transparent"
                          place="right"
                          effect="solid"
                          multiline={false}
                        >
                          <span className="tooltip-content">{`${chapter.progress.done}/${chapter.progress.all}`}</span>
                        </ReactTooltip>
                      </>
                    )}
                  </ChapterNavigationItem>
                }
              >
                <div>
                  {chapter.materials.map((material, index) => {
                    const pillName =
                      index === 0
                        ? material.pillName
                        : material.pillName !==
                          chapter.materials[index - 1].pillName
                        ? material.pillName
                        : '';

                    const lecturerMaterial =
                      material.access === MATERIAL_ACCESS.LECTURER;

                    const isFirstLecturerMaterial =
                      index !== 0 &&
                      chapter.materials[index - 1].access !==
                        MATERIAL_ACCESS.LECTURER;

                    return (
                      shouldMaterialRender(material) && (
                        <div key={material.id}>
                          {pillName && <SubjectLabel>{pillName}</SubjectLabel>}
                          {lecturerMaterial && isFirstLecturerMaterial && (
                            <LecturerMaterialBrake />
                          )}
                          <MaterialLabel
                            to={getMaterialUrl(
                              material.id,
                              `${menuItems.number}-${chapter.number}`
                            )}
                            title={material.name}
                          >
                            <MaterialIconProvider
                              material={material}
                              isLecturerMaterial={
                                material.access === MATERIAL_ACCESS.LECTURER
                              }
                            />
                            <div
                              className={
                                material.status === PASS_STATUS.NOT_VISITED
                                  ? 'not-visited material-label'
                                  : 'material-label'
                              }
                            >
                              {material.name}
                            </div>
                            <div className="status">
                              <MenuMaterialStatus status={material.status} />
                            </div>
                          </MaterialLabel>
                        </div>
                      )
                    );
                  })}
                </div>
              </ChapterCollapse>
            ) : (
              <ChapterNavigationItemDisabled>
                <div className="label">{chapter.name}</div>
                {lockedIcon}
              </ChapterNavigationItemDisabled>
            );
          })}
        </div>
      </MaterialNavigationSidebar>
      <MaterialNavigationSidebarMobile collapse={sidebarCollapse}>
        <div className="menu-items-container">
          {menuItems.chapters.map((chapter) => {
            return (
              <div key={chapter.id}>
                <ChapterNavigationItemMobile
                  id={`chapter-mobile-${chapter.id}`}
                >
                  <div className="label">{chapter.name}</div>
                  {chapter.access
                    ? chapter.progress && (
                        <ProgressIcon
                          points={chapter.progress.done}
                          maxPoints={chapter.progress.all}
                        />
                      )
                    : lockedIcon}
                </ChapterNavigationItemMobile>
                <div>
                  {chapter.materials.map((material, index) => {
                    const pillName =
                      index === 0
                        ? material.pillName
                        : material.pillName !==
                          chapter.materials[index - 1].pillName
                        ? material.pillName
                        : '';

                    return (
                      <div key={material.id}>
                        {pillName && (
                          <PillNameMobile>{pillName}</PillNameMobile>
                        )}
                        <MaterialLabelMobile
                          to={getMaterialUrl(
                            material.id,
                            `${menuItems.number}-${chapter.number}`
                          )}
                          title={material.name}
                          onClick={toggleSidebar}
                        >
                          <MaterialIconProvider
                            material={material}
                            isLecturerMaterial={
                              material.access === MATERIAL_ACCESS.LECTURER
                            }
                          />
                          <div
                            className={
                              material.status === PASS_STATUS.NOT_VISITED
                                ? 'not-visited material-label'
                                : 'material-label'
                            }
                          >
                            {material.name}
                          </div>
                          <div className="status">
                            <MenuMaterialStatus status={material.status} />
                          </div>
                        </MaterialLabelMobile>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </MaterialNavigationSidebarMobile>
    </>
  );
};

export default React.memo(MaterialNavigation);
